import { differenceInCalendarDays, differenceInMonths, format, isValid, parseISO } from 'date-fns';
import type { Claim, Order, Policy } from '@/types';
import type { ProductCase, Shipment, ShipmentStage } from '@/types/product';
import { formatAddress } from './address';
import { returnActionFor } from './returnAction';

// ---------- formatting ----------

const toDate = (value?: string | null) => {
  if (!value) return null;
  const d = parseISO(value);
  return isValid(d) ? d : null;
};

/** "2026-09-18" → "18 Sep 2026". */
export const formatDay = (value?: string | null) => {
  const d = toDate(value);
  return d ? format(d, 'dd MMM yyyy') : '';
};

/** Time left until `end`: "2y", "1y 3m", "10m", "25 days". */
export const formatTimeLeft = (end: string, now = new Date()) => {
  const endDate = toDate(end);
  if (!endDate) return '';
  const months = differenceInMonths(endDate, now);
  if (months >= 12) {
    const y = Math.floor(months / 12);
    const m = months % 12;
    return m ? `${y}y ${m}m` : `${y}y`;
  }
  if (months >= 1) return `${months}m`;
  const days = Math.max(0, differenceInCalendarDays(endDate, now));
  return `${days} ${days === 1 ? 'day' : 'days'}`;
};

/** Share of the cover period still remaining, 0–1. */
export const coverRemaining = (start: string | undefined, end: string, now = new Date()) => {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e || e <= s) return 0;
  return Math.min(1, Math.max(0, (e.getTime() - now.getTime()) / (e.getTime() - s.getTime())));
};

export const daysUntil = (value: string, now = new Date()) => {
  const d = toDate(value);
  return d ? differenceInCalendarDays(d, now) : 0;
};

/** Shopify images arrive as a URL, a JSON string or an object with `src`. */
export const imageSrc = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'object') return (value as { src?: string }).src;
  if (typeof value !== 'string') return undefined;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && 'src' in parsed) return parsed.src;
  } catch {
    // Plain URL.
  }
  return value;
};

// ---------- plan wording ----------

/** Which kind of plan a policy type is: accidental damage, extended warranty or shipping. */
export const planKind = (type?: string) => {
  const t = String(type ?? '').toLowerCase();
  if (t.includes('ship')) return 'shipping';
  if (t.includes('extend') || t.includes('manufactur') || t.includes('warrant')) return 'extended';
  return 'accidental';
};

const PLAN_LABEL = {
  accidental: 'Accidental Damage Protection',
  extended: 'Extended Warranty',
  shipping: 'Shipping Protection',
} as const;

const PLAN_NOTE = {
  accidental: 'Drops, cracks and other accidental damage',
  extended: 'Manufacturing defects · repairs up to the invoice value',
  shipping: 'Loss or damage while in transit',
} as const;

/** "1-Year Accidental Damage Protection" / "18-Month Extended Warranty". */
export const planName = (type: string | undefined, start?: string, end?: string) => {
  const label = PLAN_LABEL[planKind(type)];
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return label;
  const months = Math.round(differenceInCalendarDays(e, s) / 30.44);
  if (months >= 12 && months % 12 === 0) return `${months / 12}-Year ${label}`;
  return months > 0 ? `${months}-Month ${label}` : label;
};

const planNote = (type: string | undefined, policyNumber?: string) =>
  [PLAN_NOTE[planKind(type)], policyNumber].filter(Boolean).join(' · ');

// ---------- assembling product cases ----------

const policyDate = (policy: Policy, event: string) => policy.timeline?.find((t) => t.event === event)?.date;

const OPEN_CLAIM_STATUSES = ['submitted', 'under_review', 'in_review', 'pending', 'review'];

const claimIdLabel = (id: string) => (id.toUpperCase().startsWith('CLM') ? id.toUpperCase() : `CLM-${id.slice(0, 6).toUpperCase()}`);

function activeClaimFor(policy: Policy, claims: Claim[]): ProductCase['activeClaim'] {
  const claim = claims
    .filter((c) => String(c.policyId) === String(policy.id) || (c.policyNumber && c.policyNumber === policy.name))
    .filter((c) => c.mode !== 'resume' && OPEN_CLAIM_STATUSES.includes(String(c.status).toLowerCase()))
    .sort((a, b) => new Date(b.filedAt || 0).getTime() - new Date(a.filedAt || 0).getTime())[0];
  if (!claim) return undefined;
  const label = claimIdLabel(claim.id);
  const detail = [claim.description || claim.claimType, claim.filedAt && `filed ${formatDay(claim.filedAt)}`].filter(Boolean).join(' · ');
  return { id: claim.id, label, detail, summary: [label, detail].filter(Boolean).join(' · ') };
}

const orderPolicies = (order: Order) => (Array.isArray(order.policy) ? order.policy : order.policy ? [order.policy] : []);

const isDelivered = (order: Order) =>
  Boolean(order.delivered_at) || /deliver|fulfilled/i.test(String(order.fulfillment_status ?? ''));

/** "31 Oct, 6:15 PM". */
export const formatDayTime = (value?: string | null) => {
  const d = toDate(value);
  return d ? format(d, 'dd MMM, h:mm a') : '';
};

type CourierScan = { date?: string; activity?: string; status?: string; location?: string };

/**
 * Best-effort shipment from the order's fulfilment status and courier data. The courier
 * payload isn't documented yet, so this reads the common Shiprocket-style field names and
 * leaves out anything that's missing.
 */
function shipmentFor(order: Order): Shipment {
  const meta = order.courier_metadata ?? {};
  const status = String(meta.status ?? meta.current_status ?? order.fulfillment_status ?? '').toLowerCase();
  const stage: ShipmentStage = isDelivered(order)
    ? 'Delivered'
    : /transit|out for delivery|hub|undelivered|failed/.test(status)
      ? 'In transit'
      : /ship|dispatch|picked/.test(status)
        ? 'Shipped'
        : 'Ordered';
  const eta = toDate(meta.expected_delivery_date ?? meta.etd);
  const courier: string | undefined = meta.courier_name ?? meta.courier;
  const last = meta.current_status ?? meta.last_event;
  const scans: CourierScan[] = Array.isArray(meta.shipment_track_activities) ? meta.shipment_track_activities : Array.isArray(meta.scans) ? meta.scans : [];
  const events = scans
    .filter((scan) => scan.activity || scan.status)
    .sort((a, b) => new Date(a.date ?? 0).getTime() - new Date(b.date ?? 0).getTime())
    .map((scan, i, all) => ({
      title: String(scan.activity ?? scan.status),
      meta: [formatDayTime(scan.date), scan.location].filter(Boolean).join(' · ') || undefined,
      state: i === all.length - 1 && stage !== 'Delivered' ? ('current' as const) : ('done' as const),
    }));
  return {
    stage,
    eta: eta ? format(eta, 'EEE, dd MMM') : undefined,
    detail: [courier, last].filter(Boolean).join(' · ') || undefined,
    courier,
    awb: meta.awb_code ?? meta.awb,
    trackingUrl: meta.tracking_url,
    events: events.length ? events : undefined,
    failedAttempt: /undelivered|failed|attempt/.test(status) ? { message: String(last ?? 'The courier couldn’t deliver your order.') } : undefined,
  };
}

function protectedCase(base: Omit<ProductCase, 'state'>, policy: Policy, claims: Claim[]): ProductCase {
  const startDate = policyDate(policy, 'Coverage Started');
  return {
    ...base,
    state: 'protected',
    plan: {
      policy,
      name: planName(policy.type, startDate, policy.expiryDate),
      note: planNote(policy.type, policy.name),
      startDate,
      endDate: policy.expiryDate,
    },
    activeClaim: activeClaimFor(policy, claims),
  };
}

/** "₹6,999". */
export const formatRupees = (amount?: number) =>
  amount ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount) : '';

export const orderLabel = (order: Pick<Order, 'order_id' | 'human_readable_order_id'>) =>
  order.human_readable_order_id || `ORD-${String(order.order_id).slice(-10).toUpperCase()}`;

/** The case for one order line: protected, on the way, or delivered without a plan. */
function caseForOrder(order: Order, policiesById: Map<string, Policy>, claims: Claim[]): ProductCase {
  const linked = orderPolicies(order)
    .map((p) => policiesById.get(String(p.id)))
    .filter((p): p is Policy => Boolean(p));
  const active = linked.find((p) => p.status === 'active');
  const lapsed = linked.find((p) => p.status === 'expired');

  const base = {
    id: String(order.order_id),
    orderId: String(order.order_id),
    orderLabel: orderLabel(order),
    price: order.product_price,
    title: order.product_title,
    imageUrl: imageSrc(order.product_image) ?? imageSrc(active?.imageUrl),
    merchant: order.merchant_name ?? active?.merchantName,
    orderedAt: order.created_at,
    deliveredAt: order.delivered_at,
    deliverTo: formatAddress(order.shipping_address),
    returnAction: returnActionFor(order),
    shipment: order.fulfillment_status !== undefined || order.courier_metadata ? shipmentFor(order) : undefined,
  };

  if (active) return protectedCase(base, active, claims);
  if (!isDelivered(order) && order.fulfillment_status !== undefined) return { ...base, state: 'on_the_way' };
  return {
    ...base,
    state: 'not_protected',
    lapsedPlanName: lapsed ? planName(lapsed.type, policyDate(lapsed, 'Coverage Started'), lapsed.expiryDate) : undefined,
    returnWindow: order.returns_active !== undefined ? { open: Boolean(order.returns_active) } : undefined,
  };
}

/** One card per order on the Orders tab, newest first. */
export function buildOrderCases(policies: Policy[], orders: Order[], claims: Claim[]): ProductCase[] {
  const byId = new Map(policies.map((p) => [String(p.id), p]));
  return orders
    .map((order) => caseForOrder(order, byId, claims))
    .sort((a, b) => new Date(b.orderedAt ?? 0).getTime() - new Date(a.orderedAt ?? 0).getTime());
}

/**
 * One card per purchased product: protected (active plan), not protected (delivered, no
 * active plan) or on the way (not delivered, no active plan). Policies whose order isn't in
 * the order history still get a card of their own.
 */
export function buildProductCases(policies: Policy[], orders: Order[], claims: Claim[]): ProductCase[] {
  const byId = new Map(policies.map((p) => [String(p.id), p]));
  const used = new Set(orders.flatMap((o) => orderPolicies(o).map((p) => String(p.id))));
  const cases = orders.map((order) => caseForOrder(order, byId, claims));

  for (const policy of policies) {
    if (used.has(String(policy.id)) || policy.status !== 'active') continue;
    cases.push(
      protectedCase(
        {
          id: String(policy.id),
          title: policy.productName || 'Protected product',
          imageUrl: imageSrc(policy.imageUrl),
          merchant: policy.merchantName,
          orderedAt: policyDate(policy, 'Policy Created'),
        },
        policy,
        claims,
      ),
    );
  }

  return cases;
}
